import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Fuel, Gauge, CalendarDays, Car, ChevronDown, Palette, Type, Eye, SlidersHorizontal, Zap, DoorClosed, Tag, Link, Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { SettingsColorPicker, SettingsSlider } from '../index';
import {
  FONT_STYLES,
  BADGE_CONFIGS,
  BADGE_SHAPES,
  THEME_PRESETS,
} from '../../../constants/settings';
import {
  getTemplatesForEvent,
  getTemplateColors,
  getEventDefaults,
  getTemplateOptions,
} from '../../../hooks/useTemplateManifest';
import { getTemplateThumbnails } from '../../../services/storyApi';

// Icon mapping for toggles
const ICONS = { Fuel, Gauge, CalendarDays, Car, Zap, DoorClosed, Tag, Link };

/* ============================================================
 * TemplateThumbnailImg — real server-rendered template preview
 * Falls back to a color-based skeleton while loading
 * ============================================================ */
const TemplateThumbnailImg = ({ src, bgColor, loading: parentLoading }) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const bg = bgColor || '#1a1a2e';

  // Reset loaded state when src changes
  useEffect(() => { setImgLoaded(false); }, [src]);

  return (
    <div
      className="w-full aspect-[9/16] rounded-md overflow-hidden relative"
      style={{ background: bg }}
    >
      {src && !parentLoading ? (
        <>
          {!imgLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-muted" />
            </div>
          )}
          <img
            src={src}
            alt=""
            className={`w-full h-full object-cover transition-opacity duration-200 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
          />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-muted" />
        </div>
      )}
    </div>
  );
};

// Info toggles configuration
const INFO_TOGGLES = [
  { key: 'fuel', icon: 'Fuel', label: 'Carburant' },
  { key: 'transmission', icon: 'Gauge', label: 'Transmission' },
  { key: 'year', icon: 'CalendarDays', label: 'Année' },
  { key: 'mileage', icon: 'Car', label: 'Kilométrage' },
  { key: 'price', icon: 'Tag', label: 'Prix' },
  { key: 'power', icon: 'Zap', label: 'Puissance' },
  { key: 'doors', icon: 'DoorClosed', label: 'Portes' },
  { key: 'url', icon: 'Link', label: 'URL' },
];

// Badges per event type — only show relevant badges
const EVENT_BADGES = {
  new_vehicle: ['nouveaute', 'garantie', 'reprise'],
  price_reduction: ['promo', 'nouveaute', 'garantie'],
  sold_vehicle: ['vendu', 'garantie', 'reprise'],
};

// Size sliders — human-readable labels
const SIZE_SLIDERS = [
  { key: 'catchphrase', label: 'Texte d\'accroche' },
  { key: 'vehicle', label: 'Nom du véhicule' },
  { key: 'price', label: 'Prix' },
  { key: 'info', label: 'Informations' },
];

// Color fields — human-readable labels
const COLOR_FIELDS = [
  { key: 'background', label: 'Arrière-plan', default: '#1a1a2e' },
  { key: 'catchphrase', label: 'Accroche', default: '#cccccc' },
  { key: 'make', label: 'Marque', default: '#ffffff' },
  { key: 'model', label: 'Modèle', default: '#ffffff' },
  { key: 'accent', label: 'Prix', default: '#4ade80' },
  { key: 'info', label: 'Infos', default: '#999999' },
  { key: 'badge_bg', label: 'Fond badge', default: '#ef4444' },
  { key: 'badge_text', label: 'Texte badge', default: '#ffffff' },
];

// Fallback template color defaults (used when manifest is unavailable)
const FALLBACK_TEMPLATE_DEFAULTS = {
  'gradient-wave': { background: '#0f172a', make: '#f8fafc', model: '#f8fafc', accent: '#06b6d4', catchphrase: '#94a3b8', info: '#64748b' },
};

// Fallback event defaults
const FALLBACK_EVENT_DEFAULTS = {
  new_vehicle: { defaultCatchphrase: 'Nouvelle arrivée !', defaultBadge: 'nouveaute' },
  price_reduction: { defaultCatchphrase: 'Prix réduit !', defaultBadge: 'promo' },
  sold_vehicle: { defaultCatchphrase: 'Vendu rapidement !', defaultBadge: 'vendu' },
};

// Compare two color objects on main keys (badge colors are independent)
const MAIN_COLOR_KEYS = ['background', 'make', 'model', 'accent', 'catchphrase', 'info'];

const colorsMatch = (a, b) => {
  if (!a || !b) return false;
  return MAIN_COLOR_KEYS.every(k =>
    (a[k] || '').toLowerCase() === (b[k] || '').toLowerCase()
  );
};

/* ============================================================
 * FormSection — collapsible section with progressive disclosure
 * ============================================================ */
const FormSection = ({ title, icon: Icon, expanded, onToggle, badge, children }) => (
  <div className={`border rounded-xl overflow-hidden transition-colors ${
    expanded ? 'border-dark-border bg-dark-card/30' : 'border-dark-border/60'
  }`}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-dark-hover/40 transition-colors"
    >
      <div className="flex items-center gap-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted" />}
        <span className="text-sm font-medium text-primary">{title}</span>
        {badge && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-dark-surface text-muted font-medium">
            {badge}
          </span>
        )}
      </div>
      <ChevronDown className={`h-4 w-4 text-muted transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
    </button>
    {expanded && (
      <div className="px-4 pb-4 pt-1 space-y-5 border-t border-dark-border/50">
        {children}
      </div>
    )}
  </div>
);

/* ============================================================
 * EventStoryForm — redesigned with progressive disclosure
 * ============================================================ */
const EventStoryForm = ({ eventType, config, onChange, manifest }) => {
  const [sections, setSections] = useState({
    style: true,
    texte: true,
    affichage: false,
    avance: false,
  });
  const [forceCustomColors, setForceCustomColors] = useState(false);

  // Thumbnail URLs keyed by template ID
  const [thumbnails, setThumbnails] = useState({});
  const [thumbsLoading, setThumbsLoading] = useState(false);
  const scrollRef = useRef(null);

  // Load thumbnails when eventType changes
  useEffect(() => {
    let cancelled = false;
    setThumbsLoading(true);
    getTemplateThumbnails(eventType)
      .then((data) => {
        if (!cancelled) setThumbnails(data.thumbnails || {});
      })
      .catch(() => {
        if (!cancelled) setThumbnails({});
      })
      .finally(() => {
        if (!cancelled) setThumbsLoading(false);
      });
    return () => { cancelled = true; };
  }, [eventType]);

  // Horizontal scroll helpers
  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.offsetWidth * 0.7;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const toggleSection = (key) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Derive templates for this event type from manifest (with fallback)
  const availableTemplates = manifest
    ? getTemplatesForEvent(manifest, eventType)
    : [];

  // Helper to update a nested property
  const updateConfig = (section, key, value) => {
    const newConfig = { ...config };
    if (section) {
      newConfig[section] = { ...newConfig[section], [key]: value };
    } else {
      newConfig[key] = value;
    }
    onChange(newConfig);
  };

  // Get template color defaults from manifest or fallback
  const getDefaults = (templateId) => {
    if (manifest) {
      return getTemplateColors(manifest, templateId) || FALLBACK_TEMPLATE_DEFAULTS['gradient-wave'];
    }
    return FALLBACK_TEMPLATE_DEFAULTS[templateId] || FALLBACK_TEMPLATE_DEFAULTS['gradient-wave'];
  };

  // Get event defaults from manifest or fallback
  const eventDefaults = (manifest ? getEventDefaults(manifest, eventType) : null)
    || FALLBACK_EVENT_DEFAULTS[eventType]
    || FALLBACK_EVENT_DEFAULTS.new_vehicle;

  // Current values with defaults
  const template = config.template || 'gradient-wave';
  const colors = config.colors || {};
  const text = config.text || {};
  const badge = config.badge || {};
  const visibility = config.visibility || {};
  const sizes = config.sizes || {};
  const options = config.options || {};

  // Detect which theme preset matches current colors
  const templateDefaults = getDefaults(template);
  const activePreset = useMemo(() => {
    const c = config.colors;
    if (!c || Object.keys(c).length === 0) return 'default';
    if (colorsMatch(c, templateDefaults)) return 'default';
    for (const preset of THEME_PRESETS) {
      if (preset.id === 'default' || preset.id === 'custom' || !preset.colors) continue;
      if (colorsMatch(c, preset.colors)) return preset.id;
    }
    return 'custom';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.colors, templateDefaults]);

  // Get template-specific options from manifest
  const templateOptions = manifest ? getTemplateOptions(manifest, template) : {};

  // Handle template change — apply template defaults
  const handleTemplateChange = (templateId) => {
    const newDefaults = getDefaults(templateId);
    const newConfig = {
      ...config,
      template: templateId,
      colors: {
        ...newDefaults,
        badge_bg: config.colors?.badge_bg,
        badge_text: config.colors?.badge_text,
      },
      text: {
        ...config.text,
        catchphrase: config.text?.catchphrase || eventDefaults.defaultCatchphrase,
      },
      badge: {
        ...config.badge,
        type: config.badge?.type || eventDefaults.defaultBadge,
      },
    };
    onChange(newConfig);
  };

  // Handle theme preset change
  const handlePresetChange = (presetId) => {
    if (presetId === 'custom') {
      setForceCustomColors(true);
    } else {
      setForceCustomColors(false);
      if (presetId === 'default') {
        onChange({
          ...config,
          colors: { ...templateDefaults, badge_bg: null, badge_text: null },
        });
      } else {
        const preset = THEME_PRESETS.find(p => p.id === presetId);
        if (preset?.colors) {
          onChange({
            ...config,
            colors: { ...preset.colors },
          });
        }
      }
    }
  };

  // Reset colors to template defaults
  const resetColors = () => {
    onChange({
      ...config,
      colors: { ...templateDefaults, badge_bg: null, badge_text: null },
    });
  };

  return (
    <div className="space-y-3">
      {/* ─── Section 1: Style — template + ambiance ─── */}
      <FormSection
        title="Mise en page & ambiance"
        icon={Palette}
        expanded={sections.style}
        onToggle={() => toggleSection('style')}
      >
        {/* Template selection — horizontal scroll with real previews */}
        <div>
          <label className="block text-sm font-medium text-label mb-3">Choisir un style</label>
          <div className="relative group/scroll">
            {/* Scroll left button */}
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-0 bottom-6 z-10 w-7 flex items-center justify-center bg-gradient-to-r from-dark-card/95 to-transparent opacity-0 group-hover/scroll:opacity-100 transition-opacity"
            >
              <ChevronLeft className="w-4 h-4 text-white" />
            </button>
            {/* Scroll right button */}
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-0 bottom-6 z-10 w-7 flex items-center justify-center bg-gradient-to-l from-dark-card/95 to-transparent opacity-0 group-hover/scroll:opacity-100 transition-opacity"
            >
              <ChevronRight className="w-4 h-4 text-white" />
            </button>

            {/* Scrollable container */}
            <div
              ref={scrollRef}
              className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-dark-border scrollbar-track-transparent snap-x snap-mandatory"
              style={{ scrollbarWidth: 'thin' }}
            >
              {availableTemplates.map((t) => {
                const tColors = manifest ? getTemplateColors(manifest, t.id) : null;
                const isSelected = template === t.id;
                const thumbUrl = thumbnails[t.id];
                return (
                  <button
                    key={t.id}
                    onClick={() => handleTemplateChange(t.id)}
                    className={`relative flex-none snap-start p-1.5 border-2 rounded-xl text-center transition-all ${
                      isSelected
                        ? 'border-carmin bg-carmin/5 ring-1 ring-carmin/30'
                        : 'border-dark-border hover:border-gray-500'
                    }`}
                    style={{ width: 'calc((100% - 24px) / 3)' }}
                  >
                    {/* Check indicator */}
                    {isSelected && (
                      <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-carmin rounded-full flex items-center justify-center z-10">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {/* Real template thumbnail */}
                    <TemplateThumbnailImg
                      src={thumbUrl}
                      bgColor={tColors?.background}
                      loading={thumbsLoading}
                    />
                    {/* Template name */}
                    <div className="font-medium text-primary text-[11px] mt-1.5 leading-tight truncate">{t.name}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Template-specific options (dynamic from manifest) */}
        {Object.entries(templateOptions).map(([optKey, optDef]) => {
          if (optDef.type === 'select') {
            const currentValue = options[optKey] || optDef.default;
            return (
              <div key={optKey}>
                <label className="block text-sm font-medium text-label mb-2">{optDef.label}</label>
                <div className="flex gap-2">
                  {(optDef.choices || []).map((choice) => (
                    <button
                      key={choice.id}
                      onClick={() => updateConfig('options', optKey, choice.id)}
                      className={`flex-1 px-3 py-2 text-sm font-medium border-2 rounded-lg transition-all ${
                        currentValue === choice.id
                          ? 'border-carmin bg-carmin/5 text-carmin'
                          : 'border-dark-border text-secondary hover:border-gray-500'
                      }`}
                    >
                      {choice.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          if (optDef.type === 'slider') {
            const scale = optDef.scale || 1;
            const rawValue = options[optKey] != null ? options[optKey] : optDef.default / scale;
            const displayValue = Math.round(rawValue * scale);
            return (
              <div key={optKey}>
                <label className="block text-sm font-medium text-label mb-2">
                  {optDef.label}: {displayValue}{optDef.unit || ''}
                </label>
                <input
                  type="range"
                  min={optDef.min}
                  max={optDef.max}
                  value={displayValue}
                  onChange={(e) => updateConfig('options', optKey, parseInt(e.target.value) / scale)}
                  className="w-full h-2 bg-dark-border/50 rounded-lg appearance-none cursor-pointer accent-carmin"
                />
                <div className="flex justify-between text-xs text-muted mt-1">
                  <span>{optDef.min}{optDef.unit || ''}</span>
                  <span>{Math.round((optDef.min + optDef.max) / 2)}{optDef.unit || ''}</span>
                  <span>{optDef.max}{optDef.unit || ''}</span>
                </div>
              </div>
            );
          }

          return null;
        })}

        {/* Theme presets */}
        <div>
          <label className="block text-sm font-medium text-label mb-3">Ambiance</label>
          <div className="grid grid-cols-5 gap-1.5">
            {THEME_PRESETS.map((preset) => {
              const isActive = preset.id === 'custom' ? forceCustomColors : activePreset === preset.id && !forceCustomColors;
              return (
                <button
                  key={preset.id}
                  onClick={() => handlePresetChange(preset.id)}
                  className={`relative p-1.5 rounded-lg border-2 text-center transition-all ${
                    isActive
                      ? 'border-carmin bg-carmin/5'
                      : 'border-dark-border hover:border-gray-500'
                  }`}
                >
                  {/* Color swatches */}
                  {preset.preview ? (
                    <div className="flex items-center justify-center gap-0.5 h-5 mb-1">
                      {preset.preview.map((color, i) => (
                        <div
                          key={i}
                          className="w-3 h-3 rounded-full border border-dark-border/30"
                          style={{ background: color }}
                        />
                      ))}
                    </div>
                  ) : preset.id === 'default' ? (
                    <div className="w-full h-5 rounded-md mb-1 border-2 border-dashed border-gray-600 flex items-center justify-center">
                      <span className="text-[8px] text-dimmed">auto</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-5 mb-1">
                      <div
                        className="w-5 h-5 rounded-full border border-dark-border/30"
                        style={{ background: 'conic-gradient(#9B1B30, #1e3a8a, #c45a28, #9B1B30)' }}
                      />
                    </div>
                  )}
                  <div className="text-[10px] font-medium text-primary leading-tight truncate">{preset.name}</div>
                  {isActive && (
                    <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-carmin rounded-full flex items-center justify-center">
                      <Check className="w-2 h-2 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Color pickers — visible when "Sur mesure" is active */}
        {(activePreset === 'custom' || forceCustomColors) && (
          <div className="space-y-3 pt-3 border-t border-dark-border/50">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-label">Vos couleurs</label>
              <button
                onClick={resetColors}
                className="text-xs text-muted hover:text-primary transition-colors underline"
              >
                Réinitialiser
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {COLOR_FIELDS.map(({ key, label, default: defaultValue }) => (
                <SettingsColorPicker
                  key={key}
                  label={label}
                  value={colors[key]}
                  defaultValue={defaultValue}
                  onChange={(value) => updateConfig('colors', key, value)}
                />
              ))}
            </div>
          </div>
        )}
      </FormSection>

      {/* ─── Section 2: Texte & badge ─── */}
      <FormSection
        title="Texte & badge"
        icon={Type}
        expanded={sections.texte}
        onToggle={() => toggleSection('texte')}
      >
        {/* Font style */}
        <div>
          <label className="block text-sm font-medium text-label mb-2">Police</label>
          <div className="grid grid-cols-3 gap-1.5">
            {FONT_STYLES.map((f) => {
              const isSelected = (text.font_style || 'modern') === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => updateConfig('text', 'font_style', f.id)}
                  className={`relative p-2 border-2 rounded-lg text-center transition-all ${
                    isSelected
                      ? 'border-carmin bg-carmin/5'
                      : 'border-dark-border hover:border-gray-500'
                  }`}
                >
                  <div
                    className="text-xl font-bold text-primary leading-none mb-1"
                    style={{ fontFamily: f.font }}
                  >
                    Aa
                  </div>
                  <div className="text-[10px] font-medium text-primary leading-tight">{f.name}</div>
                  <div className="text-[9px] text-muted leading-tight">{f.desc}</div>
                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-carmin rounded-full flex items-center justify-center">
                      <Check className="w-2 h-2 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Catchphrase */}
        <div>
          <label className="block text-sm font-medium text-label mb-2">Texte d'accroche</label>
          <input
            type="text"
            value={text.catchphrase || ''}
            onChange={(e) => updateConfig('text', 'catchphrase', e.target.value)}
            placeholder={eventDefaults.defaultCatchphrase}
            className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-lg px-3 py-2.5"
          />
          <p className="text-[11px] text-muted mt-1.5">
            Apparaît en haut de votre story. Laissez vide pour le texte par défaut.
          </p>
        </div>

        {/* URL */}
        <div>
          <label className="block text-sm font-medium text-label mb-2">URL affichée</label>
          <input
            type="text"
            value={text.url || ''}
            onChange={(e) => updateConfig('text', 'url', e.target.value)}
            placeholder="www.mongarage.fr"
            className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-lg px-3 py-2.5"
          />
          <p className="text-[11px] text-muted mt-1.5">
            Apparaît en bas de votre story. Laissez vide pour utiliser l'URL de votre site.
          </p>
        </div>

        {/* Badge */}
        <div>
          <label className="block text-sm font-medium text-label mb-2">Badge</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => updateConfig('badge', 'type', 'none')}
              className={`px-3 py-2 text-sm rounded-full border-2 transition-all ${
                (!badge.type || badge.type === 'none')
                  ? 'border-carmin bg-carmin/5 text-carmin'
                  : 'border-dark-border text-secondary hover:border-gray-500'
              }`}
            >
              Aucun
            </button>
            {(EVENT_BADGES[eventType] || Object.keys(BADGE_CONFIGS)).map((key) => {
              const badgeConfig = BADGE_CONFIGS[key];
              if (!badgeConfig) return null;
              return (
              <button
                key={key}
                onClick={() => updateConfig('badge', 'type', key)}
                className={`px-3 py-2 text-xs font-bold rounded-full border-2 transition-all ${
                  badge.type === key
                    ? 'border-carmin bg-carmin/5 text-carmin'
                    : 'border-dark-border text-secondary hover:border-gray-500'
                }`}
              >
                {badgeConfig.text}
              </button>
              );
            })}
          </div>

          {/* Badge customization */}
          {badge.type && badge.type !== 'none' && (
            <div className="mt-3 space-y-3 p-3 bg-dark-hover/50 rounded-lg">
              <div>
                <label className="block text-xs text-muted mb-1">Texte personnalisé</label>
                <input
                  type="text"
                  value={badge.custom_text || ''}
                  onChange={(e) => updateConfig('badge', 'custom_text', e.target.value || null)}
                  placeholder={BADGE_CONFIGS[badge.type]?.text || 'Texte'}
                  className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-muted mb-1">Forme</label>
                <div className="flex gap-2">
                  {BADGE_SHAPES.map((shape) => (
                    <button
                      key={shape.id}
                      onClick={() => updateConfig('badge', 'shape', shape.id)}
                      className={`flex-1 px-3 py-2 text-xs font-medium border-2 transition-all ${
                        (badge.shape || 'rounded') === shape.id
                          ? 'border-carmin bg-carmin/5 text-carmin'
                          : 'border-dark-border text-secondary hover:border-gray-500'
                      } ${shape.id === 'pill' ? 'rounded-full' : shape.id === 'square' ? 'rounded-none' : 'rounded'}`}
                    >
                      {shape.label}
                    </button>
                  ))}
                </div>
              </div>
              <SettingsSlider
                label="Taille du badge"
                value={sizes.badge || 1}
                onChange={(value) => updateConfig('sizes', 'badge', value)}
                min={50}
                max={200}
              />
            </div>
          )}
        </div>
      </FormSection>

      {/* ─── Section 3: Affichage ─── */}
      <FormSection
        title="Informations affichées"
        icon={Eye}
        expanded={sections.affichage}
        onToggle={() => toggleSection('affichage')}
      >
        {/* Info toggles */}
        <div>
          <label className="block text-sm font-medium text-label mb-2">Détails du véhicule</label>
          <div className="flex flex-wrap gap-2">
            {INFO_TOGGLES.map(({ key, icon, label }) => {
              const IconComponent = ICONS[icon];
              const isEnabled = visibility[key] !== false;
              return (
                <button
                  key={key}
                  onClick={() => updateConfig('visibility', key, !isEnabled)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                    isEnabled
                      ? 'border-green-500/50 bg-green-900/20 text-green-400'
                      : 'border-dark-border text-muted hover:border-gray-500'
                  }`}
                >
                  {IconComponent && <IconComponent className="h-4 w-4" />}
                  <span className="text-sm">{label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Logo toggle + size */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-label">Votre logo</label>
              <p className="text-xs text-muted">Affiche votre logo sur la story</p>
            </div>
            <button
              type="button"
              onClick={() => updateConfig('visibility', 'logo', visibility.logo === false ? true : false)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                visibility.logo !== false ? 'bg-carmin' : 'bg-dark-hover'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ${
                visibility.logo !== false ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
          {visibility.logo !== false && (
            <SettingsSlider
              label="Taille du logo"
              value={sizes.logo || 1}
              onChange={(value) => updateConfig('sizes', 'logo', value)}
              min={50}
              max={200}
            />
          )}
        </div>

        {/* Image zoom */}
        <div>
          <label className="block text-sm font-medium text-label mb-2">
            Cadrage de la photo : {Math.round((sizes.image_zoom || 1.15) * 100)}%
          </label>
          <input
            type="range"
            min="50"
            max="150"
            value={(sizes.image_zoom || 1.15) * 100}
            onChange={(e) => updateConfig('sizes', 'image_zoom', parseInt(e.target.value) / 100)}
            className="w-full h-2 bg-dark-border/50 rounded-lg appearance-none cursor-pointer accent-carmin"
          />
          <div className="flex justify-between text-xs text-muted mt-1">
            <span>Voir tout</span>
            <span>Remplir</span>
            <span>Zoom</span>
          </div>
        </div>
      </FormSection>

      {/* ─── Section 4: Réglages fins ─── */}
      <FormSection
        title="Taille des textes"
        icon={SlidersHorizontal}
        expanded={sections.avance}
        onToggle={() => toggleSection('avance')}
        badge="Avancé"
      >
        <div className="grid grid-cols-2 gap-4">
          {SIZE_SLIDERS.map(({ key, label }) => (
            <SettingsSlider
              key={key}
              label={label}
              value={sizes[key] || 1}
              onChange={(value) => updateConfig('sizes', key, value)}
              min={50}
              max={150}
            />
          ))}
        </div>
      </FormSection>
    </div>
  );
};

export default EventStoryForm;
